import { Component } from 'react';
import lodash from 'lodash';
import uuid from 'uuid';
import styles from './Events.scss';
import DateTime from 'common/components/DateTime/DateTime';
import { formatDate } from 'pages/events/util';

const EVENT_HISTORY_ENTRY_TYPES = {
    created: 'Created the Event',
    modified: 'Modified the Event',
    signatureRequested: 'Sent Event Signature Request',
    signatureCanceled: 'Canceled Event Signature Request',
    signed: 'Signed the Event',
    exportSuccess: 'Successfully Exported Event',
    exportFail: 'Failed to Export Event',
};

const EVENT_HISTORY_TIME_FORMAT = 'h:mma';

export class EventHistoryPanel extends Component {
    render(){
        const {eventHistory=[], showDetails, timezoneOffset} = this.props;

        const reversedEventHistory = [...eventHistory].reverse();
        const historyGroupedByTimestamp = lodash.groupBy(reversedEventHistory, record => {
            const { timestamp } = record;
            return formatDate(timestamp, timezoneOffset);
        });

        const eventHistoryGroups = lodash.map(
            historyGroupedByTimestamp,
            records => <EventHistoryGroup key={uuid.v4()} records={records} timezoneOffset={timezoneOffset} />
        );
        const historyButton = <span className={styles.eventModalToggle} onClick={showDetails}>View Details</span>;
        return (
            <div className={styles.eventHistoryPanel} data-cy="EventHistoryPanel">
                <header className={styles.eventHistoryEntryHeaderHeader}>
                    <span className={styles.eventHistoryEntryCreatedy}>
                        <p className={styles.eventTypeLabel}>Event History</p>
                    </span>
                    <span className={styles.eventHistoryEntryTimestamp} data-cy="EventHistoryPanel-historyButton">
                        {historyButton}
                    </span>
                </header>
                <ul className={styles.eventHistoryGroups} data-cy="EventHistoryPanel-eventHistoryGroups">
                    { eventHistoryGroups }
                </ul>
            </div>
        );
    }
}

const EventHistoryGroup = ({records = [], timezoneOffset}) => {
    const eventHistoryEntries = records.map(record =>
        <EventHistoryEntry key={uuid.v4()} record={record} />
    );
    return (
        <li className={styles.eventHistoryElement} data-cy="EventHistoryPanel-EventHistoryGroup">
            <header className={styles.eventHistoryGroupTitle} data-cy="EventHistoryPanel-DateTime">
                <DateTime value={records[0].timestamp} timezoneOffset={timezoneOffset} time={true} format='MMM DD, YYYY' />
            </header>
            { eventHistoryEntries }
        </li>
    );
};

// Event history entries are headed by a user and the time of the entry
const EventHistoryEntry = ({record}) => {
    const {
        timestamp,
        actor,
        type: recordType,
        data,
        editNote,
    } = record;

    const entryType = EVENT_HISTORY_ENTRY_TYPES[recordType];

    const eventHistoryElements = data.map(({name, value}) => {
        if(lodash.isEmpty(value)){
            return null;
        }
        const [current, previous] = value;
        return <EventHistoryElement key={uuid.v4()} title={name} current={current} previous={previous}/>;
    });

    const statementElement = recordType !== 'signed'
        ? null
        : <EventHistoryElement title="Statement of Testament" current="I approve the details of this Event"/>;
    return (
        <ul className={styles.eventHistoryEntry} data-cy="EventHistoryPanel-ul">
            <EventHistoryEntryHeader
                name={actor}
                timestamp={timestamp}
                entryType={entryType}
                editNote={editNote}
            />
            {statementElement}
            {eventHistoryElements}
        </ul>
    );
};

const EventHistoryEntryHeader = ({name, timestamp, entryType, editNote}) => {
    return (
        <li className={styles.eventHistoryEntryHeader} data-cy="EventHistoryPanel-li">
            <header className={styles.eventHistoryEntryHeaderHeader}>
                <span className={styles.eventHistoryEntryCreatedy} data-cy="EventHistoryPanel-li-name">
                    {name}
                </span>
                <span className={styles.eventHistoryEntryTimestamp} data-cy="EventHistoryPanel-li-DateTime">
                    <DateTime
                        value={timestamp}
                        format={EVENT_HISTORY_TIME_FORMAT}
                        time={true} />
                </span>
            </header>
            <p className={styles.eventHistoryEntryType} data-cy="EventHistoryPanel-li-entryType">
                {entryType}
            </p>
            <EditNote>{editNote}</EditNote>
        </li>
    );
};

const EditNote = ({children}) => {
    if(lodash.isEmpty(children)){
        return null;
    }
    return (
        <div className={styles.editNote} data-cy="EventHistoryPanel-EditNote">
            <label>Explanation</label>
            <p>{children}</p>
        </div>
    );
};

// Event history elements are the individual data points
const EventHistoryElement = ({title, value, current, previous}) => {
    if(lodash.isNil(current)){
        return null;
    }

    const currentAndPreviousAreEqual = lodash.isEqual(current, previous);
    if(currentAndPreviousAreEqual) {
        return null;
    }

    return (
        <li className={styles.eventHistoryElement} data-cy="EventHistoryPanel-EventHistoryElement">
            <p className={styles.eventHistoryElementTitle} data-cy="EventHistoryPanel-title">
                {title}
            </p>
            <p className={styles.eventHistoryPreviousElementValue} data-cy="EventHistoryPanel-previous">
                {previous}
            </p>
            <p className={styles.eventHistoryElementValue} data-cy="EventHistoryPanel-current">
                {current}
            </p>
        </li>
    );
};
